import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleGroupShellReturn {
  handleClickToClearPartnerAxisGroupShell: () => void;
  handleKeyDownToClearPartnerAxisGroupShell: (event: KeyboardEvent) => void;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: DealLaScSection;
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

export const useHandleDealLaScSingleTableGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    tableKey,
    tableIndex,
  } = positionPropsInfo;

  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { info, changeInfo } = useTableCreatorPositionInfo();

  const handleClearGroupShell = () => {
    const clearDealPurposeGroupShell = (): ArticleEntryData => {
      const currentPartnerAxis = entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc];

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Deal Purpose": {
            ...currentPartnerAxis,
            value: [],
          },
        },
      } as ArticleEntryData;
    };
    const clearAdditionalInformationGroupShell = (): ArticleEntryData => {
      const currentPartnerAxis = entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc];

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Additional Information": {
            ...currentPartnerAxis,
            value: [],
          },
        },
      } as ArticleEntryData;
    };
    const clearOtherFinancialTermsGroupShell = (): ArticleEntryData => {
      const currentPartnerAxis = entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc];

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Other Financial Terms": {
            ...currentPartnerAxis,
            value: [],
          },
        },
      } as ArticleEntryData;
    };
    const clearPartnerAAxisGroupShell = (): ArticleEntryData => {
      const currentPartnerAxis =
        entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"];

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...currentPartnerAxis,
            "Partner A Name": {
              ...currentPartnerAxis?.["Partner A Name"],
              value: [],
            },
            "Partner A Responsibility & Rights": {
              ...currentPartnerAxis?.["Partner A Responsibility & Rights"],
              value: [],
            },
          },
        },
      } as ArticleEntryData;
    };
    const clearPartnerBAxisGroupShell = (): ArticleEntryData => {
      const currentPartnerAxis =
        entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"];

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...currentPartnerAxis,
            "Partner B Name": {
              ...currentPartnerAxis?.["Partner B Name"],
              value: [],
            },
            "Partner B Responsibility & Rights": {
              ...currentPartnerAxis?.["Partner B Responsibility & Rights"],
              value: [],
            },
          },
        },
      } as ArticleEntryData;
    };
    const clearPartnerCAxisGroupShell = (): ArticleEntryData => {
      const currentPartnerAxis =
        entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc]?.["Partner Axis"];

      return {
        ...entryData,
        [ENTRY_DATA_SECTION_KEYS.dealsLaSc]: {
          ...entryData[ENTRY_DATA_SECTION_KEYS.dealsLaSc],
          "Partner Axis": {
            ...currentPartnerAxis,
            "Partner C Name": {
              ...currentPartnerAxis?.["Partner C Name"],
              value: [],
            },
            "Partner C Responsibility & Rights": {
              ...currentPartnerAxis?.["Partner C Responsibility & Rights"],
              value: [],
            },
          },
        },
      } as ArticleEntryData;
    };

    if (info.tableKey === "Partner A Axis") {
      setEntryData(clearPartnerAAxisGroupShell());
      changeCurrentHistory({
        entryData: clearPartnerAAxisGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
      changeInfo(DEFAULT_POSITION_INFO);
    }
    if (info.tableKey === "Partner B Axis") {
      setEntryData(clearPartnerBAxisGroupShell());
      changeCurrentHistory({
        entryData: clearPartnerBAxisGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
      changeInfo(DEFAULT_POSITION_INFO);
    }
    if (info.tableKey === "Partner C Axis") {
      setEntryData(clearPartnerCAxisGroupShell());
      changeCurrentHistory({
        entryData: clearPartnerCAxisGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus({
        type: "delete",
        container: "groupShell",
      });
    }

    if (info.tableKey === "Deal Purpose") {
      setEntryData(clearDealPurposeGroupShell());
      changeCurrentHistory({
        entryData: clearDealPurposeGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
      changeInfo(DEFAULT_POSITION_INFO);
    }

    if (info.tableKey === "Other Financial Terms") {
      setEntryData(clearOtherFinancialTermsGroupShell());
      changeCurrentHistory({
        entryData: clearOtherFinancialTermsGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
      changeInfo(DEFAULT_POSITION_INFO);
    }

    if (info.tableKey === "Additional Information") {
      setEntryData(clearAdditionalInformationGroupShell());
      changeCurrentHistory({
        entryData: clearAdditionalInformationGroupShell(),
        changedDataInfo: {
          target: "groupShell",
          action: "clear",
          position: {
            groupShellKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupTableKey,
            groupShellIndex,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });
      changeFocusStatus(DEFAULT_FOCUS_STATUS);
      changeInfo(DEFAULT_POSITION_INFO);
    }
  };

  const handleClickToClearPartnerAxisGroupShell = () => {
    if (focusStatus.type === "clear") {
      handleClearGroupShell();
    }
  };

  const handleKeyDownToClearPartnerAxisGroupShell = (event: KeyboardEvent) => {
    if (focusStatus.type === "clear" && event.key === "Delete") {
      handleClearGroupShell();
    }
  };

  return {
    handleClickToClearPartnerAxisGroupShell,
    handleKeyDownToClearPartnerAxisGroupShell,
  };
};
